import {
  MediaItem,
  PendingEndlink,
  SendCommunicationAttachmentResponse
} from "@coxauto-ui/cxm/communications/interfaces";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo
} from "react";

export interface DealerPhoneValidation {
  canProceed: boolean;
  campaignApproved: boolean;
  isPhoneNumberReady: boolean;
}

export const defaultDealerPhoneValidation: DealerPhoneValidation = {
  // fail-open default: assume OK until checked to avoid flashing a blocking alert on load
  canProceed: true,
  campaignApproved: true,
  isPhoneNumberReady: true
};

export interface SelectedDealerPhoneInformation {
  phoneNumber: string;
  validation: DealerPhoneValidation;
}

export const defaultSelectedDealerPhoneInformation: SelectedDealerPhoneInformation =
  {
    phoneNumber: "",
    validation: defaultDealerPhoneValidation
  };

export interface ITextReplyContext {
  replyText: string;
  attachments: SendCommunicationAttachmentResponse[];
  selectedDealerPhoneInformation: SelectedDealerPhoneInformation;
  selectedToChannelValue: string;
  selectedTemplateLoadState: {
    hasLoaded: boolean;
    loadedTemplateId: string | null;
  };
  isTextOptInPending: boolean;
  isPhoneValidationBlocking: boolean;
  selectedMedia: MediaItem[];
  pendingEndlinks: PendingEndlink[];
  setReplyText: Dispatch<SetStateAction<string>>;
  setAttachments: Dispatch<
    SetStateAction<SendCommunicationAttachmentResponse[]>
  >;
  setSelectedDealerPhoneInformation: Dispatch<
    SetStateAction<SelectedDealerPhoneInformation>
  >;
  setSelectedToChannelValue: Dispatch<SetStateAction<string>>;
  setSelectedTemplateLoadState: Dispatch<
    SetStateAction<{
      hasLoaded: boolean;
      loadedTemplateId: string | null;
    }>
  >;
  setIsTextOptInPending: Dispatch<SetStateAction<boolean>>;
  setSelectedMedia: Dispatch<SetStateAction<MediaItem[]>>;
  setPendingEndlinks: Dispatch<SetStateAction<PendingEndlink[]>>;
}

export const TextReplyContext = createContext<ITextReplyContext | undefined>(
  undefined
);

export interface TextReplyProviderProps {
  value: ITextReplyContext;
  children: ReactNode;
}

export function TextReplyProvider({ value, children }: TextReplyProviderProps) {
  const memoizedValue = useMemo(
    () => value,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      value.replyText,
      value.attachments,
      value.selectedDealerPhoneInformation,
      value.selectedToChannelValue,
      value.selectedTemplateLoadState,
      value.isTextOptInPending,
      value.isPhoneValidationBlocking,
      value.selectedMedia,
      value.pendingEndlinks,
      value.setReplyText,
      value.setAttachments,
      value.setSelectedDealerPhoneInformation,
      value.setSelectedToChannelValue,
      value.setSelectedTemplateLoadState,
      value.setIsTextOptInPending,
      value.setSelectedMedia,
      value.setPendingEndlinks
    ]
  );

  return (
    <TextReplyContext.Provider value={memoizedValue}>
      {children}
    </TextReplyContext.Provider>
  );
}

export function useTextReply(): ITextReplyContext {
  const context = useContext(TextReplyContext);
  if (context === undefined) {
    throw new Error(
      "useTextReply must be used within a TextReplyProvider. " +
        "Make sure your component is wrapped with <TextReplyProvider>."
    );
  }
  return context;
}

export default TextReplyContext;
